import { useEffect, useMemo, useState } from "react"
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import AddIcon from "@mui/icons-material/AddOutlined"
import RemoveIcon from "@mui/icons-material/RemoveCircleOutlineOutlined"

import { billingApi } from "../../api"
import ClientSelect from "../../components/ClientSelect"
import { money } from "./InvoiceTable"
import SurchargeRow from "./SurchargeRow"
import { surchargeCents } from "./surcharge"

/**
 * An invoice for a figure, with no hours behind it.
 *
 * ── Why this is not the Prepare tab ─────────────────────────────────────────
 *
 * Prepare reads a client's unbilled time and turns it into lines — which is the
 * right screen when there are hours and the wrong one when there are not. A
 * fixed fee, a deposit, a licence, a piece of work quoted rather than tracked:
 * none of them have a timesheet, and until now the only way to bill one was to
 * set up a recurring charge and stop it after the first run.
 *
 * ── Why the amount is typed in whole currency ───────────────────────────────
 *
 * Everything below the surface is minor units, because integer cents is the only
 * way arithmetic on money stays exact. Nobody types cents, though: a field that
 * wants 250 for £2.50 produces invoices for a hundred times too much, and that
 * mistake is only visible after the money has moved.
 */
const BLANK_LINE = { description: "", amount: "", quantity: "1" }

export default function OneOffInvoiceDialog({ open, clients, defaultClientId, onClose, onDrafted, onError }) {
  const [clientId, setClientId] = useState(defaultClientId || "")
  const [lines, setLines] = useState([{ ...BLANK_LINE }])
  const [notes, setNotes] = useState("")
  const [dueInDays, setDueInDays] = useState("14")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [addSurcharge, setAddSurcharge] = useState(true)
  /* The client's own rate, fetched when one is chosen: this dialog has no
     unbilled-time call to carry the settings along with it. */
  const [settings, setSettings] = useState(null)

  useEffect(() => {
    if (!open) return
    setClientId(defaultClientId || "")
    setLines([{ ...BLANK_LINE }])
    setNotes("")
    setDueInDays("14")
    setError(null)
    setBusy(false)
  }, [open, defaultClientId])

  useEffect(() => {
    if (!open || !clientId) {
      setSettings(null)
      return
    }
    let cancelled = false
    billingApi
      .settings(clientId)
      .then((data) => {
        if (!cancelled) setSettings(data.settings || data)
      })
      .catch(() => {
        if (!cancelled) setSettings(null)
      })
    return () => {
      cancelled = true
    }
  }, [open, clientId])

  const linesTotal = useMemo(
    () =>
      lines.reduce((sum, line) => {
        const amount = Math.round(Number(line.amount) * 100)
        const quantity = Number(line.quantity)
        if (!Number.isFinite(amount) || !Number.isFinite(quantity)) return sum
        return sum + Math.round(amount * quantity)
      }, 0),
    [lines],
  )

  const surcharge = addSurcharge
    ? surchargeCents(linesTotal, {
        surchargeBps: settings?.surcharge_bps,
        surchargeFixedCents: settings?.surcharge_fixed_cents,
      })
    : 0
  const totalCents = linesTotal + surcharge

  const complete = lines.filter((line) => line.description.trim() && Number(line.amount) > 0)
  const ready = Boolean(clientId) && complete.length > 0 && totalCents > 0

  const setLine = (index, patch) =>
    setLines((current) => current.map((line, i) => (i === index ? { ...line, ...patch } : line)))

  const submit = async (event) => {
    event.preventDefault()
    if (!ready) return
    setBusy(true)
    setError(null)
    try {
      const result = await billingApi.createInvoice({
        client_id: clientId,
        notes: notes.trim() || null,
        due_in_days: Number(dueInDays) || 14,
        add_surcharge: addSurcharge,
        lines: complete.map((line) => ({
          // `fixed`, not `time`: nothing on this invoice claims any hours, so
          // nothing on a timesheet is marked as billed by drafting it.
          kind: "fixed",
          description: line.description.trim(),
          quantity: Number(line.quantity) || 1,
          unit_amount_cents: Math.round(Number(line.amount) * 100),
        })),
      })
      onDrafted(result.message || `Draft ${result.invoice?.number} created.`)
    } catch (err) {
      setError(err.userMessage)
      onError?.(err.userMessage)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <form onSubmit={submit}>
        <DialogTitle>
          One-off invoice
          <Typography variant="caption" color="text.secondary" display="block">
            A fixed amount, with no hours behind it. It is drafted here and sent from the invoice list.
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 8 }}>
              <ClientSelect
                clients={clients}
                value={clientId}
                onChange={setClientId}
                fullWidth
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Due in"
                type="number"
                value={dueInDays}
                onChange={(e) => setDueInDays(e.target.value)}
                fullWidth
                slotProps={{ htmlInput: { min: 0, max: 365 } }}
                helperText="Days"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={1.5}>
            {lines.map((line, index) => (
              <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  label={index === 0 ? "What is being billed" : ""}
                  value={line.description}
                  onChange={(e) => setLine(index, { description: e.target.value })}
                  fullWidth
                  placeholder="Implementation fee"
                  sx={{ flex: 1 }}
                />
                <TextField
                  label={index === 0 ? "Qty" : ""}
                  type="number"
                  value={line.quantity}
                  onChange={(e) => setLine(index, { quantity: e.target.value })}
                  sx={{ width: 84 }}
                  slotProps={{ htmlInput: { min: 1, step: 1 } }}
                />
                <TextField
                  label={index === 0 ? "Amount" : ""}
                  type="number"
                  value={line.amount}
                  onChange={(e) => setLine(index, { amount: e.target.value })}
                  sx={{ width: 130 }}
                  placeholder="0.00"
                  slotProps={{ htmlInput: { min: 0, step: "0.01" } }}
                />
                <Tooltip title="Remove this line">
                  <span>
                    <IconButton
                      size="small"
                      disabled={lines.length === 1}
                      onClick={() => setLines((current) => current.filter((_, i) => i !== index))}
                      aria-label={`Remove line ${index + 1}`}
                      sx={{ mt: index === 0 ? 3 : 0.5 }}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            ))}

            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setLines((current) => [...current, { ...BLANK_LINE }])}
              sx={{ alignSelf: "flex-start" }}
            >
              Add a line
            </Button>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <TextField
            label="Notes for the client"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            multiline
            rows={2}
            helperText="Printed on the invoice, so write it for them."
          />

          <Stack spacing={0.5} sx={{ mt: 2 }}>
            <SurchargeRow
              baseCents={linesTotal}
              settings={settings}
              checked={addSurcharge}
              onChange={setAddSurcharge}
              currency={settings?.currency || "USD"}
            />
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
              <Typography variant="body2" color="text.secondary">
                Total
              </Typography>
              <Typography variant="h3" component="p" sx={{ fontVariantNumeric: "tabular-nums" }}>
                {money(totalCents)}
              </Typography>
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={busy} color="inherit">
            Cancel
          </Button>
          {/* Drafted, never sent from here. Nothing leaves the building without
              somebody choosing how it is collected, which is the next dialog. */}
          <Button type="submit" variant="contained" disabled={busy || !ready}>
            {busy ? "Drafting…" : `Draft ${money(totalCents)}`}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
